import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Box,
  Typography,
  Paper,
  Grid,
  Stack,
  TextField,
  Button,
  ButtonBase,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  IconButton,
  Tooltip,
  Alert,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Autocomplete,
  RadioGroup,
  Radio,
  FormControlLabel,
  FormControl,
  FormLabel,
  Avatar,
  Divider,
  Select,
  MenuItem,
} from "@mui/material";
import {
  Search as SearchIcon,
  Add as AddIcon,
  DeleteOutline as DeleteIcon,
  EditOutlined as EditIcon,
  CloudUploadOutlined as UploadIcon,
  Person as PersonIcon,
  Check as CheckIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import { useAuth } from "../../contexts/AuthContext";
import { labOrderService, testCatalogService, labDepartmentService } from "../../services/lis";
import { patientService } from "../../services/ris";
import PatientRegistration from "../Clinician/PatientRegistration";
import PathologyOrderQuestions, {
  detectCategoriesFromTests,
  getDefaultsForCategory,
  getCategorySectionKey,
} from "./PathologyOrderQuestions";

export default function CreateLabOrder({
  isDark,
  preselectedPatient,
  preselectedReferral,
  onOrderCreated,
  onCancel,
}) {
  const { user } = useAuth();
  const fileInputRef = useRef(null);

  // Theme tokens
  const textPrimary = isDark ? "#F8FAFC" : "#0F172A";
  const textSecondary = isDark ? "#94A3B8" : "#64748B";
  const cardBg = isDark ? "#1E1E1E" : "#FFFFFF";
  const innerCardBg = isDark ? "#141414" : "#F8FAFC";
  const borderColor = isDark ? "rgba(255,255,255,0.08)" : "#E2E8F0";
  const primaryBlue = "#2563EB";

  // Helper: compute age from birthDate or age property
  const computeAge = (patient) => {
    if (!patient) return "—";
    if (patient.age !== undefined && patient.age !== null && patient.age !== "") {
      return typeof patient.age === "number"
        ? `${patient.age} ${patient.age === 1 ? "Year" : "Years"}`
        : String(patient.age);
    }
    const dob = patient.birthDate || patient.dob;
    if (!dob) return "—";
    const birth = new Date(dob);
    if (isNaN(birth.getTime())) return "—";
    const today = new Date();
    let calculated = today.getFullYear() - birth.getFullYear();
    const m = today.getMonth() - birth.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) calculated--;
    const ageVal = Math.max(0, calculated);
    return `${ageVal} ${ageVal === 1 ? "Year" : "Years"}`;
  };

  const getPhone = (patient) => {
    if (!patient) return "—";
    return patient.telephone || patient.phone || patient.phoneNumber || "—";
  };

  const getGender = (patient) => {
    if (!patient) return "—";
    const s = patient.sex || patient.gender;
    if (s === "M" || s === "Male" || s === "male") return "Male";
    if (s === "F" || s === "Female" || s === "female") return "Female";
    if (s === "O" || s === "Other" || s === "other") return "Other";
    return s || "—";
  };

  // Patient Selection
  const [patientSearch, setPatientSearch] = useState("");
  const [patientOptions, setPatientOptions] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState(preselectedPatient || null);
  const [patientLoading, setPatientLoading] = useState(false);
  const [registerModalOpen, setRegisterModalOpen] = useState(false);
  const [editPatientModalOpen, setEditPatientModalOpen] = useState(false);

  // Test Selection
  const [testSearch, setTestSearch] = useState("");
  const [testOptions, setTestOptions] = useState([]);
  const [selectedTests, setSelectedTests] = useState([]);
  const [testLoading, setTestLoading] = useState(false);
  const [addTestDialogOpen, setAddTestDialogOpen] = useState(false);
  const [departments, setDepartments] = useState([]);
  const [newTestForm, setNewTestForm] = useState({
    testCode: "",
    displayName: "",
    specimenType: "Blood",
    department: "",
  });

  // Specimen type options and department auto-mapping
  const SPECIMEN_TYPES = [
    'Blood', 'Serum', 'Plasma', 'Urine', 'Stool', 'CSF',
    'Swab', 'Sputum', 'Tissue', 'Aspirate', 'Semen',
    'Body Fluid', 'Bone Marrow'
  ];

  const SPECIMEN_DEPT_MAP = {
    Blood: 'HAEM', Serum: 'CHEM', Plasma: 'CHEM',
    Urine: 'CLIN_PATH', Stool: 'CLIN_PATH',
    CSF: 'MICRO', Swab: 'MICRO', Sputum: 'MICRO',
    Tissue: 'HISTO', Aspirate: 'HISTO', 'Bone Marrow': 'HISTO',
    Semen: 'MICRO', 'Body Fluid': 'CLIN_PATH'
  };

  // Form Fields
  const [requestingDoctor, setRequestingDoctor] = useState("");
  const [priority, setPriority] = useState("Routine");
  const [clinicalNotes, setClinicalNotes] = useState("");
  const [referralFile, setReferralFile] = useState(null);

  // DycoLIS Dynamic Clinical Ordering Requirements State
  const [clinicalRequirements, setClinicalRequirements] = useState({});
  const [validationErrors, setValidationErrors] = useState([]);

  // Status
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // Success Modal State (matches RIS pattern)
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [createdOrderDetails, setCreatedOrderDetails] = useState(null);

  const handleResetForm = () => {
    setShowSuccessModal(false);
    setCreatedOrderDetails(null);
    setSelectedPatient(null);
    setSelectedTests([]);
    setClinicalNotes("");
    setRequestingDoctor("");
    setReferralFile(null);
    setPriority("Routine");
    setClinicalRequirements({});
    setValidationErrors([]);
    setError("");
    setSuccess("");
  };

  // Synchronize requesting doctor with clinical requirements ordering physician and current user
  useEffect(() => {
    if (!requestingDoctor && user) {
      const uName = user.fullName || user.name || (user.firstName ? `${user.firstName} ${user.lastName || ''}`.trim() : '');
      if (uName) {
        setRequestingDoctor(uName);
      }
    }
  }, [user, requestingDoctor]);

  useEffect(() => {
    if (requestingDoctor && (!clinicalRequirements.orderingPhysician || clinicalRequirements.orderingPhysician !== requestingDoctor)) {
      setClinicalRequirements((prev) => ({
        ...prev,
        orderingPhysician: requestingDoctor,
      }));
    }
  }, [requestingDoctor]);

  useEffect(() => {
    if (clinicalRequirements.orderingPhysician && !requestingDoctor) {
      setRequestingDoctor(clinicalRequirements.orderingPhysician);
    }
  }, [clinicalRequirements.orderingPhysician]);

  // Search patients
  const searchPatients = useCallback(async (q) => {
    if (!q || q.length < 2) {
      setPatientOptions([]);
      return;
    }
    setPatientLoading(true);
    try {
      const res = await patientService.search({ q, limit: 10, systemSource: "LIS" });
      const patients = res?.data?.data || res?.data?.patients || [];
      setPatientOptions(patients);
    } catch (err) {
      console.error("Patient search error:", err);
    } finally {
      setPatientLoading(false);
    }
  }, []);

  // Search test catalog
  const searchTests = useCallback(async (q) => {
    setTestLoading(true);
    try {
      const res = await testCatalogService.search({ q: q || "", limit: 250 });
      setTestOptions(res?.data?.data || []);
    } catch (err) {
      setTestOptions([]);
    } finally {
      setTestLoading(false);
    }
  }, []);

  useEffect(() => {
    searchTests("");
  }, [searchTests]);

  // Fetch departments for the Add New Test dialog
  useEffect(() => {
    const fetchDepts = async () => {
      try {
        const res = await labDepartmentService.list();
        setDepartments(res?.data?.data || []);
      } catch (err) {
        console.error("Failed to load departments:", err);
      }
    };
    fetchDepts();
  }, []);

  // Debounced patient search
  useEffect(() => {
    const timeout = setTimeout(() => searchPatients(patientSearch), 300);
    return () => clearTimeout(timeout);
  }, [patientSearch, searchPatients]);

  // Debounced test search
  useEffect(() => {
    const timeout = setTimeout(() => searchTests(testSearch), 250);
    return () => clearTimeout(timeout);
  }, [testSearch, searchTests]);

  // Auto-sync patient and form fields when preselectedPatient or preselectedReferral changes
  useEffect(() => {
    if (preselectedPatient) {
      setSelectedPatient(preselectedPatient);
    }
    if (preselectedReferral?.patient) {
      setSelectedPatient(preselectedReferral.patient);
    }
    if (preselectedReferral?.requestingDoctor) {
      setRequestingDoctor(preselectedReferral.requestingDoctor);
    }
    if (preselectedReferral?.priority) {
      const p = String(preselectedReferral.priority);
      setPriority(p.charAt(0).toUpperCase() + p.slice(1).toLowerCase());
    }
    if (preselectedReferral?.clinicalNotes) {
      setClinicalNotes(preselectedReferral.clinicalNotes);
    }
  }, [preselectedPatient, preselectedReferral]);

  // Pre-populate tests from referral
  useEffect(() => {
    const reqList = preselectedReferral?.requestedTests;
    if (Array.isArray(reqList) && reqList.length > 0 && testOptions.length > 0) {
      const testsToAdd = [];
      reqList.forEach((reqTest) => {
        const reqStr = typeof reqTest === "string" ? reqTest : reqTest?.name || reqTest?.displayName || "";
        const reqLower = reqStr.toLowerCase();

        // Check if matching test in catalog
        const match = testOptions.find((opt) => {
          const optName = (opt.displayName || opt.name || "").toLowerCase();
          const optCode = (opt.testCode || "").toLowerCase();
          return (
            optName === reqLower ||
            optName.includes(reqLower) ||
            reqLower.includes(optName) ||
            (reqLower.includes("blood") && optCode === "fbc") ||
            (reqLower.includes("cbc") && optCode === "fbc") ||
            (reqLower.includes("panel") && optCode === "fbc") ||
            (reqLower.includes("liver") && optCode === "lft") ||
            (reqLower.includes("urine") && optCode === "urn") ||
            (reqLower.includes("kidney") && optCode === "eucr") ||
            (reqLower.includes("lipid") && optCode === "lipid") ||
            (reqLower.includes("sugar") && optCode === "fbs") ||
            (reqLower.includes("glucose") && optCode === "fbs")
          );
        });

        if (match) {
          if (!testsToAdd.some((t) => (t._id || t.testCode) === (match._id || match.testCode))) {
            testsToAdd.push(match);
          }
        } else if (testOptions.length > 0) {
          const defaultTest = testOptions[0];
          if (!testsToAdd.some((t) => (t._id || t.testCode) === (defaultTest._id || defaultTest.testCode))) {
            testsToAdd.push(defaultTest);
          }
        }
      });

      if (testsToAdd.length > 0) {
        setSelectedTests(testsToAdd);
      }
    }
  }, [preselectedReferral, testOptions]);

  // Add test
  const addTest = (test) => {
    if (!test || selectedTests.find((t) => (t._id || t.testCode) === (test._id || test.testCode)))
      return;
    const itemToAdd = {
      ...test,
      specimenType: test.specimenType || "Blood",
    };
    setSelectedTests((prev) => [...prev, itemToAdd]);
    setTestSearch("");
  };

  // Update specimen type for a single test item
  const handleUpdateTestSpecimenType = (testId, newSpecimenType) => {
    setSelectedTests((prev) =>
      prev.map((t) =>
        (t._id || t.testCode) === testId ? { ...t, specimenType: newSpecimenType } : t
      )
    );
  };

  // Remove test
  const removeTest = (testId) => {
    setSelectedTests((prev) => prev.filter((t) => (t._id || t.testCode) !== testId));
  };

  // File drop/upload
  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setReferralFile(file);
    }
  };

  // Submit Order
  const handleSubmit = async () => {
    setError("");
    setSuccess("");

    if (!selectedPatient) {
      setError("Please select a patient");
      return;
    }
    if (selectedTests.length === 0) {
      setError("Please add at least one test");
      return;
    }

    setValidationErrors([]);
    // Check compulsory requirements only if pathology/cytology/hematology tests are detected
    const detected = detectCategoriesFromTests(selectedTests);
    const activeCat = detected.length > 0
      ? ((clinicalRequirements.category && detected.includes(clinicalRequirements.category))
          ? clinicalRequirements.category
          : detected[0])
      : null;

    if (activeCat) {
      const missing = [];
      const userDocName = user?.fullName || user?.name || (user?.firstName ? `${user.firstName} ${user.lastName || ''}`.trim() : '');
      const doc = requestingDoctor || clinicalRequirements.orderingPhysician || userDocName;
      if (!doc && activeCat !== 'FROZEN_SECTION') {
        missing.push('Ordering physician information is required');
      }

      const sectionKey = getCategorySectionKey(activeCat);
      const defaults = getDefaultsForCategory(activeCat, selectedTests);
      const rawSection = clinicalRequirements[sectionKey] || {};
      const d = { ...defaults, ...rawSection };

      if (activeCat === 'GYN_CYTOLOGY') {
        if (!d.specimenSource) missing.push('Specimen source (Cervical, Endocervical, Vaginal, or other)');
        if (!d.collectionDevice) missing.push('Collection method/device');
        if (!d.clinicalHistory) missing.push('Relevant clinical history');
      } else if (activeCat === 'NON_GYN_CYTOLOGY') {
        if (!d.specimenType) missing.push('Specimen type');
        if (!d.anatomicSourceSite) missing.push('Specific anatomic source/site');
        if (!d.collectionMethod) missing.push('Collection method');
        if (!d.clinicalHistory) missing.push('Relevant clinical history');
        if (!d.fixativePreservative) missing.push('Fixative/preservative');
      } else if (activeCat === 'FNA_CYTOLOGY') {
        if (!d.anatomicSite) missing.push('Anatomic site');
        if (!d.collectionMethod) missing.push('Collection method');
        if (!d.clinicalIndication) missing.push('Clinical indication');
        if (!d.containerType) missing.push('Specimen/container type');
        if (!d.fixativePreservative) missing.push('Fixative/preservative');
      } else if (activeCat === 'BONE_MARROW') {
        if (!d.specimenSourceSite) missing.push('Specimen source/site');
        if (!d.clinicalIndication) missing.push('Clinical indication');
        if (!d.clinicalHistory) missing.push('Relevant clinical history');
        if (!d.boneMarrowAspirate) missing.push('Bone marrow aspirate detail');
        if (!d.containerType) missing.push('Specimen/container type');
        if (!d.anticoagulantFixative) missing.push('Anticoagulant/fixative');
      } else if (activeCat === 'PERIPHERAL_BLOOD') {
        if (!d.specimenType) missing.push('Specimen type');
        if (!d.clinicalIndication) missing.push('Clinical indication');
        if (!d.clinicalHistory) missing.push('Relevant clinical history');
        if (!d.collectionTubeAdditive) missing.push('Collection tube/additive');
      } else if (activeCat === 'FLOW_CYTOMETRY') {
        if (!d.testPanelOrdered) missing.push('Test/panel ordered');
        if (!d.specimenType) missing.push('Specimen type');
        if (!d.specimenSourceSite) missing.push('Specimen source/site');
        if (!d.clinicalIndication) missing.push('Clinical indication');
        if (!d.collectionTubeMedium) missing.push('Collection tube/transport medium');
      } else if (activeCat === 'CYTOGENETICS_MOLECULAR') {
        if (!d.testStudyOrdered) missing.push('Test/study ordered');
        if (!d.specimenType) missing.push('Specimen type');
        if (!d.specimenSourceSite) missing.push('Specimen source/site');
        if (!d.clinicalIndication) missing.push('Clinical indication');
        if (!d.clinicalHistory) missing.push('Relevant clinical history');
        if (!d.collectionTubeContainer) missing.push('Collection tube/container');
        if (!d.anticoagulantFixative) missing.push('Anticoagulant/fixative');
      } else if (activeCat === 'SURGICAL_PATHOLOGY') {
        if (!d.specimenType) missing.push('Specimen type');
        if (!d.anatomicSite) missing.push('Anatomic site');
        if (!d.procedurePerformed) missing.push('Procedure performed');
        if (!d.clinicalDiagnosisIndication) missing.push('Clinical diagnosis/indication');
        if (!d.clinicalHistory) missing.push('Relevant clinical history');
        if (!d.fixative) missing.push('Fixative');
      } else if (activeCat === 'RESECTION') {
        if (!d.specimenType) missing.push('Specimen type');
        if (!d.anatomicSite) missing.push('Anatomic site');
        if (!d.surgicalProcedure) missing.push('Surgical procedure');
        if (!d.clinicalDiagnosis) missing.push('Clinical diagnosis');
        if (!d.clinicalHistory) missing.push('Relevant clinical history');
        if (!d.fixative) missing.push('Fixative');
      } else if (activeCat === 'BREAST_PATHOLOGY') {
        if (!d.specimenType) missing.push('Specimen type');
        if (!d.breastSite) missing.push('Breast/site');
        if (!d.procedure) missing.push('Procedure');
        if (!d.clinicalIndication) missing.push('Clinical indication');
        if (!d.clinicalHistory) missing.push('Relevant clinical history');
        if (!d.fixative) missing.push('Fixative');
      } else if (activeCat === 'SPECIAL_STAINS_IHC') {
        if (!d.testStainRequested) missing.push('Test/stain requested');
        if (!d.specimenType) missing.push('Specimen type');
        if (!d.anatomicSite) missing.push('Anatomic site');
        if (!d.blockSlideId) missing.push('Specimen/block/slide identification');
        if (!d.clinicalIndication) missing.push('Clinical indication');
      } else if (activeCat === 'FROZEN_SECTION') {
        if (!d.specimenType) missing.push('Specimen type');
        if (!d.anatomicSite) missing.push('Anatomic site');
        if (!d.surgicalProcedure) missing.push('Surgical procedure');
        if (!d.clinicalQuestionIndication) missing.push('Clinical question/indication');
        if (!d.intraoperativeUrgency) missing.push('Intraoperative urgency');
        if (!d.operatingRoomLocation) missing.push('Operating room/location');
      }

      if (missing.length > 0) {
        setValidationErrors(missing);
        setError(`Please fill in required ordering requirements: ${missing.slice(0, 3).join(', ')}${missing.length > 3 ? ` and ${missing.length - 3} more` : ''}`);

        // Auto-scroll directly to the exact missing field and highlight it with red pulse
        setTimeout(() => {
          const first = (missing[0] || '').toLowerCase().replace(/[^a-z0-9]/g, '');
          let targetEl = null;

          // 1. Check data-field-name or data-field-label attributes
          const labelled = document.querySelectorAll('[data-field-name], [data-field-label]');
          for (const el of labelled) {
            const name = (el.getAttribute('data-field-name') || el.getAttribute('data-field-label') || '').toLowerCase().replace(/[^a-z0-9]/g, '');
            if (name.includes(first) || first.includes(name) || (name.length > 4 && first.includes(name.slice(0, 4)))) {
              targetEl = el.closest('.MuiGrid-item') || el.closest('[class*="MuiGrid-root"]') || el;
              break;
            }
          }

          // 2. Search through Grid items text
          if (!targetEl) {
            const gridItems = document.querySelectorAll('#pathology-ordering-questions .MuiGrid-item, #pathology-ordering-questions [class*="MuiGrid-root"]');
            for (const item of gridItems) {
              const text = item.textContent.toLowerCase().replace(/[^a-z0-9]/g, '');
              if (text.includes(first) || (first.length > 4 && text.includes(first.slice(0, 4)))) {
                targetEl = item;
                break;
              }
            }
          }

          // 3. Fallback to questions container
          if (!targetEl) {
            targetEl = document.getElementById('pathology-ordering-questions');
          }

          if (targetEl) {
            targetEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
            const input = targetEl.querySelector('input:not([type="hidden"]), textarea, .MuiSelect-select');
            if (input) {
              input.focus();
              const originalBorder = input.style.border;
              const originalBoxShadow = input.style.boxShadow;
              input.style.transition = 'all 0.3s ease';
              input.style.border = '2px solid #EF4444';
              input.style.boxShadow = '0 0 0 4px rgba(239, 68, 68, 0.4)';
              input.style.borderRadius = '8px';
              setTimeout(() => {
                if (input) {
                  input.style.border = originalBorder || '';
                  input.style.boxShadow = originalBoxShadow || '';
                }
              }, 4000);
            }
          }
        }, 100);

        return;
      }
    }

    setSubmitting(true);
    try {
      const activeSectionKey = activeCat ? getCategorySectionKey(activeCat) : null;
      const activeDefaults = activeCat ? getDefaultsForCategory(activeCat, selectedTests) : {};
      const userDocName = user?.fullName || user?.name || (user?.firstName ? `${user.firstName} ${user.lastName || ''}`.trim() : '');
      const finalDoc = requestingDoctor || clinicalRequirements?.orderingPhysician || userDocName || 'Ordering Clinician';

      const res = await labOrderService.create({
        patientId: selectedPatient._id,
        testIds: selectedTests.map((t) => t._id || t.testCode),
        tests: selectedTests.map((t) => ({
          testId: t._id || t.testCode,
          testCode: t.testCode,
          specimenType: t.specimenType || "Blood",
        })),
        specimenTypes: selectedTests.reduce((acc, t) => {
          const key = t._id || t.testCode;
          acc[key] = t.specimenType || "Blood";
          return acc;
        }, {}),
        requestingDoctor: finalDoc,
        priority: activeCat === 'FROZEN_SECTION' ? 'STAT' : priority.toUpperCase(),
        clinicalNotes,
        referralId: preselectedReferral?._id || preselectedReferral?.referralId || undefined,
        clinicalRequirements: (activeCat && Object.keys(clinicalRequirements).length > 0) ? {
          ...clinicalRequirements,
          category: activeCat,
          orderingPhysician: finalDoc,
          ...(activeSectionKey ? {
            [activeSectionKey]: {
              ...activeDefaults,
              ...(clinicalRequirements[activeSectionKey] || {})
            }
          } : {})
        } : undefined
      });

      const orderData = res?.data?.data || {};
      const orderId =
        orderData.orderNumber ||
        orderData.orderId ||
        `ORD-${new Date().getFullYear()}-${Math.floor(1000 + Math.random() * 9000)}`;

      const patName = selectedPatient.firstName
        ? `${selectedPatient.firstName} ${selectedPatient.lastName || ""}`.trim()
        : selectedPatient.patientName || selectedPatient.name || "Patient";
      const patMrn = selectedPatient.mrn || selectedPatient.patientId || "—";

      const testNames = selectedTests.map(
        (t) => t.displayName || t.name || t.testName || "Laboratory Test"
      );
      const specimenTypes =
        Array.from(
          new Set(
            selectedTests
              .map((t) => t.specimenType || t.sampleType)
              .filter(Boolean)
          )
        ).join(", ") || "Specimen";

      setCreatedOrderDetails({
        orderId,
        patientName: patName,
        patientMrn: patMrn,
        tests: testNames,
        specimenType: specimenTypes,
        priority: priority === "STAT" ? "STAT" : "Routine",
        status: "New Order",
        raw: orderData,
      });

      setShowSuccessModal(true);
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to create order");
    } finally {
      setSubmitting(false);
    }
  };

  const renderSuccessModalContent = () => (
    <Box sx={{ py: 0.5, px: 0.5, textAlign: "center", position: "relative" }}>
      {/* Close button inside modal dialog header */}
      <IconButton
        onClick={handleResetForm}
        sx={{ position: "absolute", right: -6, top: -6, color: textSecondary, p: 0.5 }}
      >
        <CloseIcon sx={{ fontSize: 18 }} />
      </IconButton>

      {/* Success checkmark with floating dots */}
      <Box sx={{ display: "flex", justifyContent: "center", mt: 0, mb: 1, position: "relative" }}>
        <Box
          sx={{
            width: 38,
            height: 38,
            borderRadius: "50%",
            bgcolor: "#10B981",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#FFFFFF",
            boxShadow: "0 4px 12px rgba(16, 185, 129, 0.25)",
          }}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="20 6 9 17 4 12" />
          </svg>
        </Box>
        {/* Floating circles decoration */}
        <Box sx={{ position: "absolute", width: 5, height: 5, borderRadius: "50%", bgcolor: "#34D399", top: 4, left: "37%" }} />
        <Box sx={{ position: "absolute", width: 4, height: 4, borderRadius: "50%", bgcolor: "#6EE7B7", top: 20, left: "34%" }} />
        <Box sx={{ position: "absolute", width: 6, height: 6, borderRadius: "50%", bgcolor: "#A7F3D0", bottom: 4, right: "35%" }} />
        <Box sx={{ position: "absolute", width: 4, height: 4, borderRadius: "50%", bgcolor: "#34D399", top: 12, right: "33%" }} />
      </Box>

      {/* Title */}
      <Typography
        variant="h6"
        sx={{
          fontFamily: "'Satoshi', sans-serif",
          fontWeight: 800,
          color: textPrimary,
          fontSize: "1.05rem",
          mb: 0.25,
          letterSpacing: "-0.02em",
        }}
      >
        Order Created Successfully!
      </Typography>

      {/* Subtitle */}
      <Typography
        variant="body2"
        sx={{
          fontFamily: "'Satoshi', sans-serif",
          fontWeight: 500,
          color: textSecondary,
          fontSize: "0.75rem",
          mb: 1.25,
          px: 1,
          lineHeight: 1.3,
        }}
      >
        The laboratory order has been successfully added to the worklist.
      </Typography>

      {/* Details Container */}
      <Box
        sx={{
          border: `1px solid ${borderColor}`,
          borderRadius: "8px",
          p: 1.2,
          mb: 1.2,
          display: "flex",
          flexDirection: "column",
          gap: 0.6,
          bgcolor: isDark ? "#141414" : "#F8FAFC",
          textAlign: "left",
        }}
      >
        {/* Patient row */}
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#64748B" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
              <circle cx="12" cy="7" r="4" />
            </svg>
            <Typography sx={{ fontFamily: "'Satoshi', sans-serif", fontWeight: 700, color: textSecondary, fontSize: "0.85rem" }}>
              Patient
            </Typography>
          </Box>
          <Box sx={{ textAlign: "right" }}>
            <Typography sx={{ fontFamily: "'Satoshi', sans-serif", fontWeight: 700, color: textPrimary, fontSize: "0.875rem" }}>
              {createdOrderDetails?.patientName}
            </Typography>
            <Typography sx={{ fontFamily: "'Satoshi', sans-serif", fontWeight: 500, color: textSecondary, fontSize: "0.75rem", mt: 0.25 }}>
              {createdOrderDetails?.patientMrn}
            </Typography>
          </Box>
        </Box>

        <Divider sx={{ borderColor: borderColor }} />

        {/* Order ID row */}
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#64748B" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="4" y1="9" x2="20" y2="9" />
              <line x1="4" y1="15" x2="20" y2="15" />
              <line x1="10" y1="3" x2="8" y2="21" />
              <line x1="16" y1="3" x2="14" y2="21" />
            </svg>
            <Typography sx={{ fontFamily: "'Satoshi', sans-serif", fontWeight: 700, color: textSecondary, fontSize: "0.85rem" }}>
              Order ID
            </Typography>
          </Box>
          <Typography sx={{ fontFamily: "'Satoshi', sans-serif", fontWeight: 800, color: "#2563EB", fontSize: "0.875rem" }}>
            {createdOrderDetails?.orderId}
          </Typography>
        </Box>

        <Divider sx={{ borderColor: borderColor }} />

        {/* Tests / Panels row */}
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexShrink: 0 }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#64748B" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
              <polyline points="14 2 14 8 20 8" />
              <line x1="16" y1="13" x2="8" y2="13" />
              <line x1="16" y1="17" x2="8" y2="17" />
              <polyline points="10 9 9 9 8 9" />
            </svg>
            <Typography sx={{ fontFamily: "'Satoshi', sans-serif", fontWeight: 700, color: textSecondary, fontSize: "0.85rem" }}>
              Tests / Panels
            </Typography>
          </Box>
          <Box sx={{ textAlign: "right" }}>
            {createdOrderDetails?.tests?.map((tName, i) => (
              <Typography key={i} sx={{ fontFamily: "'Satoshi', sans-serif", fontWeight: 700, color: textPrimary, fontSize: "0.85rem", lineHeight: 1.3 }}>
                {tName}
              </Typography>
            ))}
          </Box>
        </Box>

        <Divider sx={{ borderColor: borderColor }} />

        {/* Specimen row */}
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#64748B" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M10 2v7.31a2 2 0 0 1-.27.99L5.42 18A2 2 0 0 0 7.17 21h9.66a2 2 0 0 0 1.75-3l-4.31-7.7a2 2 0 0 1-.27-.99V2" />
              <line x1="8" y1="2" x2="16" y2="2" />
              <line x1="7" y1="15" x2="17" y2="15" />
            </svg>
            <Typography sx={{ fontFamily: "'Satoshi', sans-serif", fontWeight: 700, color: textSecondary, fontSize: "0.85rem" }}>
              Specimen
            </Typography>
          </Box>
          <Typography sx={{ fontFamily: "'Satoshi', sans-serif", fontWeight: 700, color: textPrimary, fontSize: "0.875rem" }}>
            {createdOrderDetails?.specimenType}
          </Typography>
        </Box>

        <Divider sx={{ borderColor: borderColor }} />

        {/* Priority row */}
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#64748B" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z" />
              <line x1="4" y1="22" x2="4" y2="15" />
            </svg>
            <Typography sx={{ fontFamily: "'Satoshi', sans-serif", fontWeight: 700, color: textSecondary, fontSize: "0.85rem" }}>
              Priority
            </Typography>
          </Box>
          <Typography
            sx={{
              fontFamily: "'Satoshi', sans-serif",
              fontWeight: 700,
              color: createdOrderDetails?.priority === "STAT" ? "#EF4444" : "#0D9488",
              fontSize: "0.875rem",
            }}
          >
            {createdOrderDetails?.priority === "STAT" ? "STAT" : "Routine"}
          </Typography>
        </Box>

        <Divider sx={{ borderColor: borderColor }} />

        {/* Status row */}
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#64748B" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
              <line x1="16" y1="2" x2="16" y2="6" />
              <line x1="8" y1="2" x2="8" y2="6" />
              <line x1="3" y1="10" x2="21" y2="10" />
            </svg>
            <Typography sx={{ fontFamily: "'Satoshi', sans-serif", fontWeight: 700, color: textSecondary, fontSize: "0.85rem" }}>
              Status
            </Typography>
          </Box>
          <Box
            sx={{
              px: 1.75,
              py: 0.5,
              borderRadius: "20px",
              bgcolor: isDark ? "rgba(37, 99, 235, 0.15)" : "#EFF6FF",
              border: "1.5px solid #BFDBFE",
              color: isDark ? "#60A5FA" : "#1E40AF",
              fontSize: "0.75rem",
              fontWeight: 700,
              fontFamily: "'Satoshi', sans-serif",
            }}
          >
            New Order
          </Box>
        </Box>
      </Box>

      {/* Action Buttons: side by side so vertical height never exceeds screen */}
      <Box sx={{ display: "flex", gap: 1, mt: 1.2, mb: 0.25 }}>
        <Button
          variant="outlined"
          onClick={handleResetForm}
          fullWidth
          startIcon={
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="12" y1="5" x2="12" y2="19" />
              <line x1="5" y1="12" x2="19" y2="12" />
            </svg>
          }
          sx={{
            fontFamily: "'Satoshi', sans-serif",
            fontWeight: 700,
            textTransform: "none",
            color: "#00665C",
            borderColor: "#00665C",
            border: "1.5px solid",
            borderRadius: "8px",
            height: "32px",
            fontSize: "0.8rem",
            bgcolor: cardBg,
            "&:hover": {
              borderColor: "#004D47",
              bgcolor: isDark ? "rgba(13, 148, 136, 0.1)" : "#F0FDFA",
            },
          }}
        >
          Create Another
        </Button>
        <Button
          variant="contained"
          onClick={() => {
            onOrderCreated?.(createdOrderDetails, "samples");
          }}
          fullWidth
          startIcon={
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
              <line x1="9" y1="9" x2="15" y2="9" />
              <line x1="9" y1="13" x2="15" y2="13" />
              <line x1="9" y1="17" x2="15" y2="17" />
            </svg>
          }
          sx={{
            fontFamily: "'Satoshi', sans-serif",
            fontWeight: 700,
            textTransform: "none",
            color: "#FFFFFF",
            bgcolor: "#005B4F",
            borderRadius: "8px",
            height: "32px",
            fontSize: "0.8rem",
            boxShadow: "none",
            "&:hover": {
              bgcolor: "#004D47",
              boxShadow: "none",
            },
          }}
        >
          Go to Samples
        </Button>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ width: "100%", pb: 1 }}>
      {/* Top Header Bar: Title, Breadcrumbs, Priority & Action Buttons */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 1,
          mb: 1,
        }}
      >
        <Box>
          <Typography
            sx={{
              fontFamily: "'Satoshi', sans-serif",
              fontSize: "1.05rem",
              fontWeight: 800,
              color: textPrimary,
              lineHeight: 1.2,
              letterSpacing: "-0.01em",
            }}
          >
            Create New Laboratory Order
          </Typography>

          <Stack direction="row" spacing={0.6} alignItems="center" sx={{ mt: 0.25 }}>
            <Typography
              onClick={onCancel}
              sx={{
                fontFamily: "'Satoshi', sans-serif",
                fontSize: "0.7rem",
                color: textSecondary,
                cursor: "pointer",
                "&:hover": { color: primaryBlue, textDecoration: "underline" },
              }}
            >
              Dashboard
            </Typography>
            <Typography sx={{ color: textSecondary, fontSize: "0.68rem" }}>›</Typography>
            <Typography
              onClick={onCancel}
              sx={{
                fontFamily: "'Satoshi', sans-serif",
                fontSize: "0.7rem",
                color: textSecondary,
                cursor: "pointer",
                "&:hover": { color: primaryBlue, textDecoration: "underline" },
              }}
            >
              Create Orders
            </Typography>
            <Typography sx={{ color: textSecondary, fontSize: "0.68rem" }}>›</Typography>
            <Typography
              sx={{
                fontFamily: "'Satoshi', sans-serif",
                fontSize: "0.7rem",
                color: primaryBlue,
                fontWeight: 700,
              }}
            >
              New Order
            </Typography>
          </Stack>
        </Box>

        {/* Priority Selector & Action Buttons */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {/* Priority Pill Toggle */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              bgcolor: innerCardBg,
              p: "2px",
              borderRadius: "6px",
              border: `1px solid ${borderColor}`,
            }}
          >
            <ButtonBase
              onClick={() => setPriority("Routine")}
              sx={{
                px: 1.2,
                py: "3px",
                borderRadius: "4px",
                fontSize: "0.72rem",
                fontWeight: 700,
                fontFamily: "'Satoshi', sans-serif",
                bgcolor: priority === "Routine" ? "#2563EB" : "transparent",
                color: priority === "Routine" ? "#FFFFFF" : textSecondary,
                transition: "all 0.15s ease",
              }}
            >
              Routine
            </ButtonBase>
            <ButtonBase
              onClick={() => setPriority("STAT")}
              sx={{
                px: 1.2,
                py: "3px",
                borderRadius: "4px",
                fontSize: "0.72rem",
                fontWeight: 700,
                fontFamily: "'Satoshi', sans-serif",
                bgcolor: priority === "STAT" ? "#DC2626" : "transparent",
                color: priority === "STAT" ? "#FFFFFF" : textSecondary,
                transition: "all 0.15s ease",
              }}
            >
              STAT
            </ButtonBase>
          </Box>

          <Button
            variant="outlined"
            size="small"
            onClick={onCancel}
            sx={{
              borderRadius: "6px",
              borderColor: borderColor,
              color: textSecondary,
              textTransform: "none",
              fontFamily: "'Satoshi', sans-serif",
              fontWeight: 600,
              fontSize: "0.75rem",
              height: 28,
              px: 1.5,
              "&:hover": {
                borderColor: textPrimary,
                color: textPrimary,
                bgcolor: isDark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.03)",
              },
            }}
          >
            Cancel
          </Button>

          <Button
            variant="contained"
            size="small"
            onClick={handleSubmit}
            disabled={submitting || !selectedPatient || selectedTests.length === 0}
            startIcon={!submitting && <CheckIcon sx={{ fontSize: 15 }} />}
            sx={{
              borderRadius: "6px",
              bgcolor: "#2563EB",
              color: "#FFFFFF",
              textTransform: "none",
              fontFamily: "'Satoshi', sans-serif",
              fontWeight: 700,
              fontSize: "0.75rem",
              height: 28,
              px: 2,
              boxShadow: "none",
              "&:hover": {
                bgcolor: "#1D4ED8",
              },
              "&.Mui-disabled": {
                bgcolor: isDark ? "#2D3748" : "#E2E8F0",
                color: isDark ? "#718096" : "#94A3B8",
              },
            }}
          >
            {submitting ? (
              <CircularProgress size={16} sx={{ color: "#ffffff" }} />
            ) : (
              "Create Order"
            )}
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 1, py: 0.25, px: 1, borderRadius: "6px", fontSize: "0.75rem" }}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 1, py: 0.25, px: 1, borderRadius: "6px", fontSize: "0.75rem" }}>
          {success}
        </Alert>
      )}

      {/* Main Single-Screen 2-Column Workstation Layout */}
      <Grid container spacing={1} sx={{ alignItems: "stretch" }}>
        {/* Left Column: Patient Info + Clinical Context & Referral */}
        <Grid item xs={12} md={5}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1, height: "100%" }}>
            {/* Card 1: Patient Information */}
            <Paper
              elevation={0}
              sx={{
                p: 1.25,
                borderRadius: "8px",
                bgcolor: cardBg,
                border: `1px solid ${borderColor}`,
              }}
            >
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.75 }}>
                <Typography
                  sx={{
                    fontFamily: "'Satoshi', sans-serif",
                    fontSize: "0.8rem",
                    fontWeight: 700,
                    color: textPrimary,
                  }}
                >
                  1. Patient Information
                </Typography>

                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => setRegisterModalOpen(true)}
                  startIcon={<AddIcon sx={{ fontSize: 14 }} />}
                  sx={{
                    borderRadius: "5px",
                    borderColor: "#3B82F6",
                    color: "#2563EB",
                    textTransform: "none",
                    fontFamily: "'Satoshi', sans-serif",
                    fontWeight: 600,
                    fontSize: "0.7rem",
                    px: 1,
                    py: 0,
                    height: 24,
                    whiteSpace: "nowrap",
                    "&:hover": {
                      borderColor: "#1D4ED8",
                      bgcolor: isDark ? "rgba(37,99,235,0.1)" : "#EFF6FF",
                    },
                  }}
                >
                  Register Patient
                </Button>
              </Box>

              <Autocomplete
                fullWidth
                size="small"
                options={patientOptions}
                getOptionLabel={(opt) =>
                  `${opt.firstName || ""} ${opt.lastName || ""} (${opt.mrn || opt.patientId || ""})`
                }
                value={selectedPatient}
                onChange={(_, val) => {
                  if (val) setSelectedPatient(val);
                }}
                inputValue={patientSearch}
                onInputChange={(_, val) => setPatientSearch(val)}
                loading={patientLoading}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size="small"
                    placeholder="Search patient by name, ID or phone..."
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <>
                          <InputAdornment position="start">
                            <SearchIcon sx={{ color: textSecondary, fontSize: 16 }} />
                          </InputAdornment>
                          {params.InputProps.startAdornment}
                        </>
                      ),
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "5px",
                        fontFamily: "'Satoshi', sans-serif",
                        fontSize: "0.78rem",
                        bgcolor: isDark ? "#141414" : "#FFFFFF",
                        height: 28,
                        "& fieldset": { borderColor },
                        "&:hover fieldset": { borderColor: "#3B82F6" },
                      },
                      "& .MuiOutlinedInput-input": {
                        py: "3px !important",
                      },
                    }}
                  />
                )}
                renderOption={(props, opt) => (
                  <li {...props} key={opt._id}>
                    <Box>
                      <Typography sx={{ fontWeight: 600, fontSize: "0.78rem", color: textPrimary }}>
                        {opt.firstName} {opt.lastName}
                      </Typography>
                      <Typography sx={{ fontSize: "0.7rem", color: textSecondary }}>
                        ID: {opt.patientId || opt.mrn} • {getGender(opt)} •{" "}
                        {opt.telephone || opt.phone || opt.phoneNumber || "No phone"}
                      </Typography>
                    </Box>
                  </li>
                )}
                noOptionsText="No matching patients found"
              />

              {/* Selected Patient Compact Summary */}
              {selectedPatient && (
                <Box
                  sx={{
                    p: 0.85,
                    mt: 0.75,
                    borderRadius: "6px",
                    bgcolor: innerCardBg,
                    border: `1px solid ${borderColor}`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 1,
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
                    <Avatar
                      sx={{
                        width: 28,
                        height: 28,
                        bgcolor: isDark ? "rgba(37, 99, 235, 0.2)" : "#EFF6FF",
                        color: "#2563EB",
                        border: "1px solid #DBEAFE",
                        flexShrink: 0,
                      }}
                    >
                      <PersonIcon sx={{ fontSize: 18 }} />
                    </Avatar>
                    <Box sx={{ minWidth: 0 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexWrap: "wrap" }}>
                        <Typography
                          sx={{
                            fontFamily: "'Satoshi', sans-serif",
                            fontWeight: 700,
                            fontSize: "0.82rem",
                            color: textPrimary,
                            lineHeight: 1.2,
                          }}
                        >
                          {selectedPatient.firstName} {selectedPatient.lastName}
                        </Typography>
                        <Typography
                          sx={{
                            fontFamily: "'Satoshi', sans-serif",
                            fontSize: "0.7rem",
                            fontWeight: 600,
                            color: "#2563EB",
                            bgcolor: isDark ? "rgba(37,99,235,0.15)" : "#EFF6FF",
                            px: 0.6,
                            py: "1px",
                            borderRadius: "3px",
                          }}
                        >
                          {selectedPatient.patientId || selectedPatient.mrn || "PT-250512-001"}
                        </Typography>
                      </Box>
                      <Typography sx={{ fontSize: "0.7rem", color: textSecondary, mt: "2px" }}>
                        {getGender(selectedPatient)} • {computeAge(selectedPatient)} • {getPhone(selectedPatient)}
                      </Typography>
                    </Box>
                  </Box>

                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => setEditPatientModalOpen(true)}
                    startIcon={<EditIcon sx={{ fontSize: 13 }} />}
                    sx={{
                      borderRadius: "5px",
                      borderColor: "#3B82F6",
                      color: "#2563EB",
                      textTransform: "none",
                      fontFamily: "'Satoshi', sans-serif",
                      fontWeight: 600,
                      fontSize: "0.7rem",
                      px: 1,
                      py: "2px",
                      height: 24,
                      flexShrink: 0,
                      "&:hover": {
                        borderColor: "#1D4ED8",
                        bgcolor: isDark ? "rgba(37,99,235,0.1)" : "#EFF6FF",
                      },
                    }}
                  >
                    Edit
                  </Button>
                </Box>
              )}
            </Paper>

            {/* Card 2: Clinical Context & Referral */}
            <Paper
              elevation={0}
              sx={{
                p: 1.25,
                borderRadius: "8px",
                bgcolor: cardBg,
                border: `1px solid ${borderColor}`,
                flex: 1,
                display: "flex",
                flexDirection: "column",
                gap: 1,
              }}
            >
              <Typography
                sx={{
                  fontFamily: "'Satoshi', sans-serif",
                  fontSize: "0.8rem",
                  fontWeight: 700,
                  color: textPrimary,
                }}
              >
                2. Clinical Context & Referral
              </Typography>

              <Box>
                <Typography
                  sx={{
                    fontFamily: "'Satoshi', sans-serif",
                    fontSize: "0.72rem",
                    fontWeight: 600,
                    color: textSecondary,
                    mb: 0.3,
                  }}
                >
                  Requesting Doctor (Optional)
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Enter doctor's name"
                  value={requestingDoctor}
                  onChange={(e) => setRequestingDoctor(e.target.value)}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "5px",
                      fontFamily: "'Satoshi', sans-serif",
                      fontSize: "0.75rem",
                      bgcolor: isDark ? "#141414" : "#FFFFFF",
                      height: 28,
                      "& fieldset": { borderColor },
                      "&:hover fieldset": { borderColor: "#3B82F6" },
                    },
                    "& .MuiOutlinedInput-input": {
                      py: "3px !important",
                    },
                  }}
                />
              </Box>

              <Box>
                <Typography
                  sx={{
                    fontFamily: "'Satoshi', sans-serif",
                    fontSize: "0.72rem",
                    fontWeight: 600,
                    color: textSecondary,
                    mb: 0.3,
                  }}
                >
                  Clinical Notes (Optional)
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  rows={2}
                  size="small"
                  placeholder="Enter symptoms, diagnosis, or clinical notes..."
                  value={clinicalNotes}
                  onChange={(e) => setClinicalNotes(e.target.value)}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "5px",
                      fontFamily: "'Satoshi', sans-serif",
                      fontSize: "0.75rem",
                      bgcolor: isDark ? "#141414" : "#FFFFFF",
                      "& fieldset": { borderColor },
                      "&:hover fieldset": { borderColor: "#3B82F6" },
                    },
                  }}
                />
              </Box>

              <Box>
                <Typography
                  sx={{
                    fontFamily: "'Satoshi', sans-serif",
                    fontSize: "0.72rem",
                    fontWeight: 600,
                    color: textSecondary,
                    mb: 0.3,
                  }}
                >
                  Referral Document (Optional)
                </Typography>
                <input
                  type="file"
                  ref={fileInputRef}
                  style={{ display: "none" }}
                  onChange={handleFileChange}
                  accept=".pdf,.jpg,.jpeg,.png"
                />
                <Box
                  onClick={() => fileInputRef.current?.click()}
                  sx={{
                    p: 0.75,
                    borderRadius: "6px",
                    border: `1.5px dashed ${isDark ? "#334155" : "#BFDBFE"}`,
                    bgcolor: isDark ? "rgba(37, 99, 235, 0.05)" : "#F8FAFC",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 0.75,
                    cursor: "pointer",
                    transition: "all 0.2s ease",
                    "&:hover": {
                      borderColor: "#3B82F6",
                      bgcolor: isDark ? "rgba(37, 99, 235, 0.1)" : "#EFF6FF",
                    },
                  }}
                >
                  <UploadIcon sx={{ color: "#3B82F6", fontSize: 18 }} />
                  <Typography
                    sx={{
                      fontFamily: "'Satoshi', sans-serif",
                      fontSize: "0.75rem",
                      color: textPrimary,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {referralFile ? (
                      <Box component="span" sx={{ color: "#16A34A", fontWeight: 700 }}>
                        ✓ {referralFile.name}
                      </Box>
                    ) : (
                      <>
                        <Box component="span" sx={{ color: "#2563EB", fontWeight: 700, mr: 0.5 }}>
                          Click to upload
                        </Box>
                        referral (PDF, JPG, PNG)
                      </>
                    )}
                  </Typography>
                  {referralFile && (
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        setReferralFile(null);
                      }}
                      sx={{ p: "2px", color: "#EF4444" }}
                    >
                      <CloseIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                  )}
                </Box>
              </Box>
            </Paper>
          </Box>
        </Grid>

        {/* Right Column: Test Selection & Selected Tests */}
        <Grid item xs={12} md={7}>
          <Paper
            elevation={0}
            sx={{
              p: 1.25,
              borderRadius: "8px",
              bgcolor: cardBg,
              border: `1px solid ${borderColor}`,
              height: "100%",
              display: "flex",
              flexDirection: "column",
            }}
          >
            {/* Search Test Input + Add New Test Button */}
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              sx={{ mb: 1 }}
            >
              <Typography
                sx={{
                  fontFamily: "'Satoshi', sans-serif",
                  fontSize: "0.8rem",
                  fontWeight: 700,
                  color: textPrimary,
                  whiteSpace: "nowrap",
                }}
              >
                3. Test Selection
              </Typography>

              <Autocomplete
                fullWidth
                size="small"
                options={testOptions}
                getOptionLabel={(opt) => `${opt.displayName || opt.testName || opt.name || ""}`}
                onChange={(_, val) => {
                  if (val) addTest(val);
                }}
                inputValue={testSearch}
                onInputChange={(_, val) => setTestSearch(val)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size="small"
                    placeholder="Search test by name or code..."
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <>
                          <InputAdornment position="start">
                            <SearchIcon sx={{ color: textSecondary, fontSize: 16 }} />
                          </InputAdornment>
                          {params.InputProps.startAdornment}
                        </>
                      ),
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "5px",
                        fontFamily: "'Satoshi', sans-serif",
                        fontSize: "0.78rem",
                        bgcolor: isDark ? "#141414" : "#FFFFFF",
                        height: 28,
                        "& fieldset": { borderColor },
                        "&:hover fieldset": { borderColor: "#3B82F6" },
                      },
                      "& .MuiOutlinedInput-input": {
                        py: "3px !important",
                      },
                    }}
                  />
                )}
                renderOption={(props, opt) => (
                  <li {...props} key={opt._id || opt.testCode}>
                    <Box sx={{ py: 0.25 }}>
                      <Typography sx={{ fontWeight: 600, fontSize: "0.78rem", color: textPrimary }}>
                        {opt.displayName || opt.testName}
                      </Typography>
                      <Typography sx={{ fontSize: "0.68rem", color: textSecondary }}>
                        Code: {opt.testCode} • Specimen: {opt.specimenType} • Dept:{" "}
                        {opt.department?.name || opt.department || "General"}
                      </Typography>
                    </Box>
                  </li>
                )}
                noOptionsText="No tests found"
              />

              <Button
                variant="outlined"
                size="small"
                onClick={() => setAddTestDialogOpen(true)}
                startIcon={<AddIcon sx={{ fontSize: 14 }} />}
                sx={{
                  borderRadius: "5px",
                  borderColor: "#3B82F6",
                  color: "#2563EB",
                  textTransform: "none",
                  fontFamily: "'Satoshi', sans-serif",
                  fontWeight: 600,
                  fontSize: "0.72rem",
                  px: 1.25,
                  height: 28,
                  whiteSpace: "nowrap",
                  flexShrink: 0,
                  "&:hover": {
                    borderColor: "#1D4ED8",
                    bgcolor: isDark ? "rgba(37,99,235,0.1)" : "#EFF6FF",
                  },
                }}
              >
                Add Test
              </Button>
            </Stack>

            {/* Selected Tests Header */}
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.5 }}>
              <Typography
                sx={{
                  fontFamily: "'Satoshi', sans-serif",
                  fontSize: "0.75rem",
                  fontWeight: 700,
                  color: textPrimary,
                }}
              >
                Selected Tests ({selectedTests.length})
              </Typography>
              {selectedTests.length > 0 && (
                <Typography
                  sx={{
                    fontFamily: "'Satoshi', sans-serif",
                    fontSize: "0.7rem",
                    color: "#2563EB",
                    fontWeight: 700,
                  }}
                >
                  {selectedTests.length} item{selectedTests.length > 1 ? "s" : ""} in order
                </Typography>
              )}
            </Box>

            {/* Selected Tests Table Container */}
            <TableContainer
              sx={{
                flex: 1,
                minHeight: 180,
                maxHeight: "calc(100vh - 230px)",
                borderRadius: "6px",
                border: `1px solid ${borderColor}`,
                overflow: "auto",
              }}
            >
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow sx={{ bgcolor: isDark ? "#171717" : "#F8FAFC" }}>
                    <TableCell sx={{ fontWeight: 700, color: textSecondary, fontSize: "0.68rem", py: "3px !important", px: "6px !important", borderBottom: `1px solid ${borderColor}`, textTransform: "uppercase" }}>Test</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: textSecondary, fontSize: "0.68rem", py: "3px !important", px: "6px !important", borderBottom: `1px solid ${borderColor}`, textTransform: "uppercase", width: 140 }}>Specimen Type</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: textSecondary, fontSize: "0.68rem", py: "3px !important", px: "6px !important", borderBottom: `1px solid ${borderColor}`, textTransform: "uppercase" }}>Department</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, color: textSecondary, fontSize: "0.68rem", py: "3px !important", px: "6px !important", borderBottom: `1px solid ${borderColor}`, textTransform: "uppercase", width: 50 }}>Action</TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {selectedTests.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} align="center" sx={{ py: 3, color: textSecondary, fontSize: "0.75rem", borderBottom: 0 }}>
                        No tests selected. Use the search bar above to add tests.
                      </TableCell>
                    </TableRow>
                  ) : (
                    selectedTests.map((test) => (
                      <TableRow
                        key={test._id || test.testCode}
                        hover
                        sx={{
                          height: 28,
                          "&:last-child td": { borderBottom: 0 },
                        }}
                      >
                        <TableCell sx={{ fontSize: "0.75rem", fontWeight: 600, color: textPrimary, py: "2px !important", px: "6px !important", borderBottom: `1px solid ${borderColor}` }}>
                          {test.displayName || test.testName}
                        </TableCell>

                        <TableCell sx={{ py: "2px !important", px: "6px !important", borderBottom: `1px solid ${borderColor}` }}>
                          <FormControl size="small" fullWidth>
                            <Select
                              value={test.specimenType || "Blood"}
                              onChange={(e) =>
                                handleUpdateTestSpecimenType(
                                  test._id || test.testCode,
                                  e.target.value
                                )
                              }
                              sx={{
                                fontFamily: "'Satoshi', sans-serif",
                                fontSize: "0.72rem",
                                bgcolor: isDark ? "#141414" : "#FFFFFF",
                                color: textPrimary,
                                borderRadius: "4px",
                                height: 22,
                                "& .MuiSelect-select": { py: "1px !important", px: "6px !important" },
                                "& .MuiOutlinedInput-notchedOutline": {
                                  borderColor: isDark ? "rgba(255,255,255,0.12)" : "#CBD5E1",
                                },
                              }}
                            >
                              {SPECIMEN_TYPES.map((st) => (
                                <MenuItem key={st} value={st} sx={{ fontSize: "0.75rem" }}>
                                  {st}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </TableCell>

                        <TableCell sx={{ fontSize: "0.72rem", color: textSecondary, py: "2px !important", px: "6px !important", borderBottom: `1px solid ${borderColor}` }}>
                          {test.department?.name || test.department || "Haematology"}
                        </TableCell>

                        <TableCell align="right" sx={{ py: "2px !important", px: "6px !important", borderBottom: `1px solid ${borderColor}` }}>
                          <IconButton
                            size="small"
                            onClick={() => removeTest(test._id || test.testCode)}
                            sx={{
                              width: 22,
                              height: 22,
                              p: 0,
                              color: "#EF4444",
                              "&:hover": {
                                bgcolor: isDark ? "rgba(239,68,68,0.1)" : "#FEF2F2",
                              },
                            }}
                          >
                            <DeleteIcon sx={{ fontSize: 14 }} />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>
      </Grid>

      {/* Full-Width Dynamic Pathology / Cytology / Hematology Ordering Requirements Section */}
      {detectCategoriesFromTests(selectedTests).length > 0 && (
        <Box sx={{ mt: 1.5 }}>
          <PathologyOrderQuestions
            isDark={isDark}
            selectedTests={selectedTests}
            clinicalRequirements={clinicalRequirements}
            onChange={setClinicalRequirements}
            validationErrors={validationErrors}
          />
          {/* Bottom Action Bar for Easy Order Submission after Scrolling */}
          <Box
            sx={{
              mt: 1.5,
              p: 1.25,
              borderRadius: "8px",
              bgcolor: cardBg,
              border: `1px solid ${borderColor}`,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="body2" sx={{ color: textSecondary, fontSize: "0.8rem" }}>
              Please review all clinical ordering questions above before submitting this order.
            </Typography>
            <Stack direction="row" spacing={1}>
              {onCancel && (
                <Button
                  variant="outlined"
                  size="small"
                  onClick={onCancel}
                  sx={{
                    borderRadius: "6px",
                    borderColor,
                    color: textSecondary,
                    textTransform: "none",
                    fontSize: "0.78rem",
                    height: 32,
                    px: 2,
                    "&:hover": { borderColor: "#94A3B8" },
                  }}
                >
                  Cancel
                </Button>
              )}
              <Button
                variant="contained"
                size="small"
                onClick={handleSubmit}
                disabled={submitting || selectedTests.length === 0 || !selectedPatient}
                sx={{
                  borderRadius: "6px",
                  bgcolor: primaryBlue,
                  color: "#ffffff",
                  textTransform: "none",
                  fontWeight: 700,
                  fontSize: "0.78rem",
                  height: 32,
                  px: 2.5,
                  boxShadow: "none",
                  "&:hover": { bgcolor: "#1D4ED8" },
                }}
              >
                {submitting ? <CircularProgress size={16} sx={{ color: "#ffffff" }} /> : "Create Order"}
              </Button>
            </Stack>
          </Box>
        </Box>
      )}

      {/* Modal: Register Patient */}
      {registerModalOpen && (
        <PatientRegistration
          open={registerModalOpen}
          systemSource="LIS"
          onClose={() => setRegisterModalOpen(false)}
          onSuccess={(newPt) => {
            if (newPt) setSelectedPatient(newPt);
            setRegisterModalOpen(false);
          }}
          onCreateOrder={(newPt) => {
            if (newPt) setSelectedPatient(newPt);
            setRegisterModalOpen(false);
          }}
        />
      )}

      {/* Modal: Edit Patient */}
      {editPatientModalOpen && (
        <PatientRegistration
          open={editPatientModalOpen}
          editPatient={selectedPatient}
          systemSource="LIS"
          onClose={() => setEditPatientModalOpen(false)}
          onSuccess={(updatedPt) => {
            if (updatedPt) setSelectedPatient(updatedPt);
            setEditPatientModalOpen(false);
          }}
          onCreateOrder={(updatedPt) => {
            if (updatedPt) setSelectedPatient(updatedPt);
            setEditPatientModalOpen(false);
          }}
        />
      )}

      {/* Modal: Add New Test to Catalog */}
      {/* Modal: Add New Test to Catalog */}
      <Dialog
        open={addTestDialogOpen}
        onClose={() => setAddTestDialogOpen(false)}
        maxWidth={false}
        PaperProps={{
          sx: {
            width: { xs: "95vw", sm: "520px" },
            maxWidth: "540px !important",
            maxHeight: "85vh",
            bgcolor: cardBg,
            backgroundImage: "none",
            borderRadius: "14px",
            border: `1px solid ${borderColor}`,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          },
        }}
      >
        <DialogTitle
          sx={{
            px: 2.5,
            py: 1.25,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            borderBottom: `1px solid ${borderColor}`,
            fontFamily: "'Satoshi', sans-serif",
            fontWeight: 700,
            color: textPrimary,
            fontSize: "1.05rem",
          }}
        >
          Add New Test to Catalog
          <IconButton size="small" onClick={() => setAddTestDialogOpen(false)} sx={{ color: textSecondary }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ px: 2.5, py: 1.75, overflowY: "auto" }}>
          <Grid container spacing={1.5} sx={{ mt: 0.25 }}>
            {/* Display Name — first, since most users know the test name */}
            <Grid item xs={12}>
              <TextField
                label="Test Name *"
                value={newTestForm.displayName}
                onChange={(e) =>
                  setNewTestForm((p) => ({ ...p, displayName: e.target.value }))
                }
                fullWidth
                size="small"
                placeholder="e.g., Full Blood Count, Breast Biopsy"
                InputProps={{ sx: { fontFamily: "'Satoshi', sans-serif" } }}
              />
            </Grid>
            {/* Specimen Type — dropdown with search */}
            <Grid item xs={6}>
              <Autocomplete
                freeSolo
                options={SPECIMEN_TYPES}
                value={newTestForm.specimenType}
                onChange={(_, val) => {
                  const specimen = val || '';
                  const suggestedDeptCode = SPECIMEN_DEPT_MAP[specimen] || '';
                  const suggestedDept = departments.find(d => d.code === suggestedDeptCode);
                  setNewTestForm((p) => ({
                    ...p,
                    specimenType: specimen,
                    department: suggestedDept?._id || suggestedDept?.code || p.department
                  }));
                }}
                onInputChange={(_, val) => {
                  setNewTestForm((p) => ({ ...p, specimenType: val || '' }));
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Specimen Type *"
                    size="small"
                    placeholder="Select or type..."
                    InputProps={{ ...params.InputProps, sx: { fontFamily: "'Satoshi', sans-serif" } }}
                  />
                )}
                size="small"
              />
            </Grid>
            {/* Test Code — optional, auto-generates if blank */}
            <Grid item xs={6}>
              <TextField
                label="Test Code (optional)"
                value={newTestForm.testCode}
                onChange={(e) =>
                  setNewTestForm((p) => ({ ...p, testCode: e.target.value }))
                }
                fullWidth
                size="small"
                placeholder="Auto-generated if blank"
                helperText="Leave blank to auto-generate"
                InputProps={{ sx: { fontFamily: "'Satoshi', sans-serif" } }}
                FormHelperTextProps={{ sx: { fontSize: '0.65rem', mt: 0.3 } }}
              />
            </Grid>
            {/* Department — dropdown populated from API, auto-selected by specimen type */}
            <Grid item xs={12}>
              <Autocomplete
                options={departments}
                getOptionLabel={(opt) => typeof opt === 'string' ? opt : (opt.name || '')}
                value={departments.find(d => d._id === newTestForm.department || d.code === newTestForm.department) || null}
                onChange={(_, val) => {
                  setNewTestForm((p) => ({ ...p, department: val?._id || val?.code || '' }));
                }}
                isOptionEqualToValue={(opt, val) => opt._id === val?._id || opt.code === val?.code}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Department *"
                    size="small"
                    placeholder="Auto-selected by specimen type"
                    InputProps={{ ...params.InputProps, sx: { fontFamily: "'Satoshi', sans-serif" } }}
                  />
                )}
                renderOption={(props, option) => (
                  <li {...props} key={option._id || option.code}>
                    <Box>
                      <Typography sx={{ fontFamily: "'Satoshi', sans-serif", fontSize: '0.875rem', fontWeight: 600 }}>
                        {option.name}
                      </Typography>
                      {option.description && (
                        <Typography sx={{ fontFamily: "'Satoshi', sans-serif", fontSize: '0.7rem', color: textSecondary }}>
                          {option.description}
                        </Typography>
                      )}
                    </Box>
                  </li>
                )}
                size="small"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 2.5, py: 1.25, borderTop: `1px solid ${borderColor}`, display: "flex", justifyContent: "flex-end", gap: 1 }}>
          <Button
            onClick={() => setAddTestDialogOpen(false)}
            sx={{ color: textSecondary, textTransform: "none", fontFamily: "'Satoshi', sans-serif" }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={!newTestForm.displayName}
            onClick={async () => {
              if (!newTestForm.displayName) return;
              // Auto-generate test code from display name if blank
              let testCode = newTestForm.testCode.trim();
              if (!testCode) {
                // Take initials of each word, max 8 chars
                testCode = newTestForm.displayName
                  .replace(/[^a-zA-Z0-9\s]/g, '')
                  .split(/\s+/)
                  .filter(Boolean)
                  .map(w => w[0])
                  .join('')
                  .toUpperCase()
                  .slice(0, 8);
                // If too short, use first 6 chars of first word
                if (testCode.length < 2) {
                  testCode = newTestForm.displayName.replace(/[^a-zA-Z0-9]/g, '').toUpperCase().slice(0, 6);
                }
              }
              try {
                const res = await testCatalogService.create({
                  ...newTestForm,
                  testCode: testCode.toUpperCase(),
                });
                const created = res?.data?.data || {
                  _id: `test-${Date.now()}`,
                  ...newTestForm,
                  testCode: testCode.toUpperCase(),
                };
                setTestOptions((prev) => [created, ...prev]);
                addTest(created);
              } catch (err) {
                const fallback = {
                  _id: `test-${Date.now()}`,
                  ...newTestForm,
                  testCode: testCode.toUpperCase(),
                };
                setTestOptions((prev) => [fallback, ...prev]);
                addTest(fallback);
              }
              setAddTestDialogOpen(false);
              setNewTestForm({
                testCode: "",
                displayName: "",
                specimenType: "Blood",
                department: "",
              });
            }}
            sx={{
              bgcolor: "#2563EB",
              borderRadius: "8px",
              textTransform: "none",
              fontWeight: 600,
              fontFamily: "'Satoshi', sans-serif",
              "&:hover": { bgcolor: "#1D4ED8" },
            }}
          >
            Add Test
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success Modal Overlay (Zero-Scroll Compact Layout) */}
      <Dialog
        open={showSuccessModal}
        onClose={handleResetForm}
        maxWidth={false}
        PaperProps={{
          sx: {
            width: { xs: "95vw", sm: "400px" },
            maxWidth: "410px !important",
            borderRadius: "14px",
            p: 1.25,
            overflow: "hidden !important",
            bgcolor: cardBg,
            backgroundImage: "none",
            border: `1px solid ${borderColor}`,
            boxShadow: isDark
              ? "0 10px 40px rgba(0,0,0,0.8)"
              : "0 10px 40px rgba(0,0,0,0.1)",
          },
        }}
      >
        {renderSuccessModalContent()}
      </Dialog>
    </Box>
  );
}
